<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService?.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Listings'"
      [subtitle]="propertyLabel"
      [subtitleIcon]="'assets/svg/location-outline.svg'"
      [canGoBack]="true"
      [showLoadingSpinner]="isSaving" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>
    @if (isLoading) {
      <div class="loading-container">
        <tidy-loader />
      </div>
    } @else {
      <tidy-card>
        <tidy-row class="extra-bottom-padding" [alignLastItemRight]="true">
          <tidy-text [header]="'Connected Listings'" />
          <tidy-small-button
            class="compact"
            [text]="'Add Listing'"
            (buttonClick)="goToAddListing()" />
        </tidy-row>
        @for (listing of listings; track listing.id) {
          <div class="listing-row">
            <span class="platform-avatar" [ngClass]="listing.platform">
              @if (listing.logo) {
                <img [src]="listing.logo" [alt]="listing.platformLabel" />
              }
            </span>
            <div class="listing-row-info">
              <tidy-text
                [actionBody]="listing.platformLabel"
                (action)="goToListingDetail(listing)"
                class="white-link" />
              @if (listing.statusDescription) {
                <tidy-text [helper]="listing.statusDescription" />
              }
              @if (listing.rawStatus !== 'connecting' && listing.lastOptimizedLabel) {
                <tidy-text [helper]="listing.lastOptimizedLabel" class="listing-last-optimized" />
              }
            </div>
            @if (listing.rawStatus === 'connecting') {
              <span class="listing-status-connecting">
                <span class="status-glow-dot connecting-dot"></span>
                <tidy-text [body]="'Connecting'" />
              </span>
            } @else {
              <tidy-select
                class="listing-status-select"
                [items]="listing.isDirectBooking ? directBookingStatusItems : statusItems"
                [form]="statusControls[listing.id]"
                (optionChange)="onStatusSelect(listing, $event)" />
            }
          </div>
        }
      </tidy-card>

      <tidy-row [align]="'center'" class="extra-bottom-padding">
        <tidy-text [helper]="'Edits below apply to all connected listings for this property.'" />
      </tidy-row>

      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Listing Details'" />
        </tidy-row>
        <form [formGroup]="form">
          <tidy-row class="field-top-padding extra-bottom-padding">
            <span class="field-with-dot field-label-row">
              <tidy-text [fieldLabel]="'Title'" />
              <sync-status-dot [sync]="syncDot('title')" />
              <platform-pills [key]="'title'" [showAll]="true" />
            </span>
            <tidy-input
              formControlName="title"
              [form]="form.controls.title"
              [icon]="'assets/svg/create-outline.svg'" />
          </tidy-row>
          <tidy-row class="extra-bottom-padding">
            <span class="field-with-dot field-label-row">
              <tidy-text [fieldLabel]="'Description'" />
              <sync-status-dot [sync]="syncDot('description')" />
              <platform-pills [key]="'description'" [showAll]="true" />
            </span>
            <tidy-textarea
              formControlName="description"
              [form]="form.controls.description"
              [icon]="'assets/svg/document-text-outline.svg'" />
          </tidy-row>
          <div class="field-pair">
            <div>
              <span class="field-with-dot field-label-row">
                <tidy-text [fieldLabel]="'Security Deposit ($)'" />
                <sync-status-dot [sync]="syncDot('security_deposit')" />
                <platform-pills [key]="'security_deposit'" [showAll]="true" />
              </span>
              <tidy-input
                [type]="'number'"
                formControlName="securityDeposit"
                [form]="form.controls.securityDeposit"
                [icon]="'assets/svg/shield-checkmark-outline.svg'" />
            </div>
            <div>
              <span class="field-with-dot field-label-row">
                <tidy-text [fieldLabel]="'Square Feet'" />
              </span>
              <tidy-input
                [type]="'number'"
                formControlName="squareFeet"
                [form]="form.controls.squareFeet"
                [icon]="'assets/svg/resize-outline.svg'" />
            </div>
          </div>
          <div class="field-pair">
            <div>
              <span class="field-with-dot field-label-row">
                <tidy-text [fieldLabel]="'Bedrooms'" />
                @if (roomStructureIsAuthoritative) {
                  <sync-status-dot [sync]="syncDot('spaces')" />
                  <platform-pills [key]="'spaces'" [showAll]="true" />
                }
              </span>
              <tidy-input
                [type]="'number'"
                formControlName="bedrooms"
                [form]="form.controls.bedrooms"
                [icon]="'assets/svg/bed-outline.svg'" />
              @if (roomStructureIsAuthoritative) {
                <tidy-text [smallHelper]="'Counted from the rooms in Sleeping Arrangements.'" class="field-hint" />
              }
            </div>
            <tidy-input
              [label]="'Bathrooms'"
              [type]="'number'"
              formControlName="bathrooms"
              [form]="form.controls.bathrooms"
              [icon]="'assets/svg/water-outline.svg'" />
          </div>
          <div class="field-pair">
            <div>
              <span class="field-with-dot field-label-row">
                <tidy-text [fieldLabel]="'Beds'" />
                <sync-status-dot [sync]="syncDot('spaces')" />
                <platform-pills [key]="'spaces'" [showAll]="true" />
              </span>
              <tidy-input
                [type]="'number'"
                [form]="bedsControl"
                [formControl]="bedsControl"
                [icon]="'assets/svg/bed-outline.svg'" />
              <tidy-text [smallHelper]="'Total from the bed types set in Sleeping Arrangements.'" class="field-hint" />
            </div>
            <div>
              <span class="field-with-dot field-label-row">
                <tidy-text [fieldLabel]="'Max Guests'" />
                <sync-status-dot [sync]="syncDot('max_guests')" />
                <platform-pills [key]="'max_guests'" [showAll]="true" />
              </span>
              <tidy-input
                [type]="'number'"
                formControlName="maxGuests"
                [form]="form.controls.maxGuests"
                [icon]="'assets/svg/people-outline.svg'" />
            </div>
          </div>
          <div class="field-pair">
            <tidy-select
              [label]="'Property Type'"
              [items]="propertyTypeItems"
              [form]="form.controls.propertyType"
              [formControl]="form.controls.propertyType" />
            <tidy-select
              [label]="'Place Type'"
              [items]="placeTypeItems"
              [form]="form.controls.placeType"
              [formControl]="form.controls.placeType" />
          </div>
        </form>
      </tidy-card>

      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <span class="field-with-dot">
            <tidy-text [header]="'Amenities'" />
            <sync-status-dot [sync]="syncDot('amenities')" />
            <platform-pills [key]="'amenities'" [showAll]="true" />
          </span>
          <tidy-text [helper]="selectedAmenitiesCount + (' selected' | translate)" class="amenities-count" />
        </tidy-row>
        <div class="amenities-grid">
          @for (amenity of amenities; track amenity.slug) {
            <button
              type="button"
              class="amenity-chip"
              [class.selected]="amenityState[amenity.slug]"
              (click)="toggleAmenity(amenity.slug)">
              <span class="material-icons amenity-icon">{{ amenity.icon }}</span>
              <span class="amenity-label">{{ amenity.label | translate }}</span>
              @if (amenityState[amenity.slug]) {
                <span class="material-icons amenity-check">check</span>
              }
            </button>
          }
        </div>
      </tidy-card>

      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Policies'" />
        </tidy-row>
        <tidy-row class="field-top-padding extra-bottom-padding">
          <tidy-select
            [label]="'Cancellation Policy'"
            [items]="cancellationPolicyItems"
            [form]="form.controls.cancellationPolicy"
            [formControl]="form.controls.cancellationPolicy" />
        </tidy-row>
        <tidy-row>
          <span class="field-with-dot field-label-row">
            <tidy-text [fieldLabel]="'House Rules'" />
            <sync-status-dot [sync]="syncDot('house_rules')" />
            <platform-pills [key]="'house_rules'" [showAll]="true" />
          </span>
        </tidy-row>
        @for (control of houseRuleControls; track control) {
          <div class="editable-list-row">
            <tidy-input
              [formControl]="control"
              [form]="control"
              [icon]="'assets/svg/list-outline.svg'" />
            <button
              type="button"
              class="editable-list-remove"
              (click)="removeHouseRule($index)">
              <span class="material-icons">close</span>
            </button>
          </div>
        }
        <button type="button" class="editable-list-add" (click)="addHouseRule()">
          <span class="material-icons">add</span>
          <span>{{ 'Add House Rule' | translate }}</span>
        </button>
        <tidy-row class="extra-top-padding">
          <tidy-text [fieldLabel]="'Safety'" />
        </tidy-row>
        <div class="amenities-grid">
          @for (item of safetyItems; track item.slug) {
            <button
              type="button"
              class="amenity-chip"
              [class.selected]="isSafetySelected(item.slug)"
              (click)="toggleSafetyItem(item.slug)">
              <span class="material-icons amenity-icon">{{ item.icon }}</span>
              <span class="amenity-label">{{ item.label | translate }}</span>
              @if (isSafetySelected(item.slug)) {
                <span class="material-icons amenity-check">check</span>
              }
            </button>
          }
        </div>
      </tidy-card>

      <tidy-card>
        <tidy-row>
          <tidy-text [header]="'Neighborhood'" />
        </tidy-row>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [smallHelper]="'Tell guests what the area around the property is like.'" class="field-hint" />
        </tidy-row>
        <tidy-textarea
          [formControl]="form.controls.neighborhoodDescription"
          [form]="form.controls.neighborhoodDescription"
          [icon]="'assets/svg/map-outline.svg'" />
      </tidy-card>

      <tidy-card>
        <tidy-row class="extra-bottom-padding" [alignLastItemRight]="true">
          <tidy-text [header]="'Listing Highlights'" />
          <tidy-small-button
            class="compact"
            [text]="'Add Highlight'"
            [disabled]="!canAddHighlight"
            (buttonClick)="addHighlight()" />
        </tidy-row>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [smallHelper]="'Up to 3 short highlights shown near the top of your listing.'" class="field-hint" />
        </tidy-row>
        @for (highlight of highlightControls; track highlight) {
          <div class="editable-list-row">
            <div class="highlight-fields">
              <tidy-input
                [label]="'Title'"
                [formControl]="highlight.title"
                [form]="highlight.title"
                [submitted]="highlightsSubmitted"
                [errorMessage]="'Add a title for this highlight'"
                [icon]="'assets/svg/star-outline.svg'" />
              <tidy-input
                [label]="'Details'"
                [formControl]="highlight.body"
                [form]="highlight.body"
                [submitted]="highlightsSubmitted"
                [errorMessage]="'Add details for this highlight'"
                [icon]="'assets/svg/create-outline.svg'" />
            </div>
            <button
              type="button"
              class="editable-list-remove"
              (click)="removeHighlight($index)">
              <span class="material-icons">close</span>
            </button>
          </div>
        }
        @if (!highlightControls.length) {
          <tidy-row>
            <tidy-text [helper]="'No highlights yet — add up to 3.'" />
          </tidy-row>
        }
      </tidy-card>

      @if (bedroomRooms.length) {
        <tidy-card>
          <tidy-row>
            <span class="field-with-dot">
              <tidy-text [header]="'Sleeping Arrangements'" />
              <sync-status-dot [sync]="syncDot('spaces')" />
              <platform-pills [key]="'spaces'" [showAll]="true" />
            </span>
          </tidy-row>
          <tidy-row class="extra-bottom-padding">
            <tidy-text [smallHelper]="'Set the bed types in each bedroom. Rooms are managed in Photos below.'" class="field-hint" />
          </tidy-row>
          @for (room of bedroomRooms; track room.key) {
            <div class="photo-room">
              <div class="photo-room-header">
                <tidy-text [body]="room.name" class="medium-weight" />
              </div>
              <div class="bed-type-row">
                @for (bedType of bedTypes; track bedType.value) {
                  <div class="bed-type-chip" [class.selected]="bedCount(room, bedType.value) > 0">
                    <span class="bed-type-label">{{ bedType.viewValue | translate }}</span>
                    <button
                      type="button"
                      class="bed-count-button"
                      [disabled]="bedCount(room, bedType.value) === 0"
                      (click)="changeBedCount(room, bedType.value, -1)">
                      <span class="material-icons">remove</span>
                    </button>
                    <span class="bed-count">{{ bedCount(room, bedType.value) }}</span>
                    <button
                      type="button"
                      class="bed-count-button"
                      (click)="changeBedCount(room, bedType.value, 1)">
                      <span class="material-icons">add</span>
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </tidy-card>
      }

      <tidy-card>
        <tidy-row>
          <span class="field-with-dot">
            <tidy-text [header]="'Photos'" />
            <sync-status-dot [sync]="syncDot('photos')" />
            <platform-pills [key]="'photos'" [showAll]="true" />
          </span>
        </tidy-row>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [smallHelper]="'Drag photos to reorder. Captions currently sync to Airbnb only.'" class="field-hint" />
        </tidy-row>
        @for (room of rooms; track room.key) {
          <div class="photo-room">
            <div class="photo-room-header">
              <tidy-text [body]="room.name" class="medium-weight" />
            </div>
            <div
              class="photo-grid"
              cdkDropList
              cdkDropListOrientation="mixed"
              [cdkDropListData]="room.photos"
              (cdkDropListDropped)="onPhotoDrop(room, $event)">
              @for (photo of room.photos; track photo.id) {
                <div class="photo-tile" cdkDrag [cdkDragData]="photo">
                  <div class="photo-image-wrap" cdkDragHandle>
                    <img [src]="photo.url" [alt]="room.name" />
                    <span class="photo-drag-handle material-icons">drag_indicator</span>
                    <button
                      type="button"
                      class="photo-remove"
                      (click)="removePhoto(room, photo)">
                      <span class="material-icons">close</span>
                    </button>
                  </div>
                  @if (photoRefusals[photo.id]) {
                    <div class="photo-refused">
                      <span class="material-icons">error_outline</span>
                      <span>{{ photoRefusals[photo.id] }}</span>
                    </div>
                  }
                  <tidy-textarea
                    [label]="'Caption'"
                    [formControl]="captionControls[photo.id]"
                    [form]="captionControls[photo.id]" />
                </div>
              }
              <button type="button" class="photo-add-tile" (click)="addPhoto(room)">
                <span class="material-icons">add_photo_alternate</span>
                <span class="photo-add-label">{{ 'Add Photo' | translate }}</span>
              </button>
            </div>
          </div>
        }
        <button type="button" class="editable-list-add" (click)="addRoom()">
          <span class="material-icons">add</span>
          <span>{{ 'Add Bedroom' | translate }}</span>
        </button>
      </tidy-card>

      <!-- The Optimizer page's settings, so the builder shows every field a listing has. -->
      <app-revenue-booking-settings-page [embeddedAddressId]="addressId" />
    }
  </tidy-wrapper>
</ng-template>

@if (!windowService?.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
